import React, { useEffect, useRef, useState } from "react"
import { Link, useHistory, useLocation, useParams } from "react-router-dom"
import moment from "moment"

import loader from "@/assets/img/loader.svg"
import brandImage from "@/assets/img/login/brand.svg"

import "@/assets/scss/pages/login.scss"

import Api from "../../../_helpers/Api"
import { useLanguageContext } from "../../../_providers/Language"
import { trans } from "../../../_providers/Translation"
import ActivationError from "../../modals/messages/ActivationError"
import useOnLogin from "./useOnLogin"
import {
  CODE_LENGTH,
  RESEND_COOLDOWN,
  authPrefix,
  formatSeconds,
  parseSecondsLeft,
  sanitizeCode,
  twoFactorPath,
} from "./helpers"

const emptyCode = () => new Array(CODE_LENGTH).fill("")

function TwoFactor() {
  const languages = useLanguageContext()
  const history = useHistory()
  const location = useLocation()
  const params = useParams()

  const onLogin = useOnLogin()

  const searchParams = new URLSearchParams(location.search)

  const { role, challenge } = params
  const tenant = searchParams.get("tenant")

  const activationErrorModalRef = useRef(null)
  const inputsRef = useRef([])
  const deadlineRef = useRef(null)
  const submittedRef = useRef(null)

  const [lang, setLang] = useState(searchParams.get("lang") || "en")

  const [code, setCode] = useState(emptyCode)

  // null = още не се знае колко време остава
  const [secondsLeft, setSecondsLeft] = useState(null)

  const [loading, setLoading] = useState(true)
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [sentTo, setSentTo] = useState(null)

  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)

  const expired = secondsLeft !== null && secondsLeft <= 0
  const disabled = loading || verifying || expired

  useEffect(() => {
    if (!challenge) {
      history.replace("/login/" + (role || "partners"))
      return
    }

    loadTimeLeft()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge])

  // брояч на оставащото време
  useEffect(() => {
    const tick = () => {
      if (deadlineRef.current === null) {
        return
      }

      let left = Math.max(
        0,
        Math.ceil((deadlineRef.current - new Date().getTime()) / 1000)
      )

      setSecondsLeft((prev) => (prev === left ? prev : left))
    }

    // по-често от секунда, за да е точен и след като табът е бил на заден план
    let interval = setInterval(tick, 500)

    return () => {
      clearInterval(interval)
    }
  }, [])

  // брояч за повторно изпращане
  useEffect(() => {
    if (cooldown <= 0) {
      return
    }

    let interval = setInterval(() => {
      setCooldown((prev) => (prev > 1 ? prev - 1 : 0))
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [cooldown])

  // при изтичане на времето полетата отпадат
  useEffect(() => {
    if (expired) {
      setCode(emptyCode())
      submittedRef.current = null
    }
  }, [expired])

  // автоматично изпращане при попълнен код
  useEffect(() => {
    let value = code.join("")

    if (value.length !== CODE_LENGTH) {
      return
    }

    if (disabled || submittedRef.current === value) {
      return
    }

    handleVerify(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, disabled])

  useEffect(() => {
    window.addEventListener("storage", onStorageUpdate)
    return () => {
      window.removeEventListener("storage", onStorageUpdate)
    }
  }, [])

  const setDeadline = (seconds) => {
    if (seconds === null || seconds === undefined) {
      deadlineRef.current = null
      setSecondsLeft(null)
      return
    }

    deadlineRef.current = new Date().getTime() + seconds * 1000
    setSecondsLeft(Math.max(0, Math.floor(seconds)))
  }

  const focusInput = (index) => {
    let input = inputsRef.current[index]

    if (input) {
      input.focus()
      input.select()
    }
  }

  const getErrorMessage = (err, fallback) => {
    let data = err?.response?.data

    if (data) {
      if (typeof data.message === "string" && data.message) {
        return data.message
      }

      if (data.errors && typeof data.errors === "object") {
        let first = Object.values(data.errors)[0]

        if (Array.isArray(first) && first[0]) {
          return first[0]
        }

        if (typeof first === "string") {
          return first
        }
      }
    }

    return fallback
  }

  const loadTimeLeft = () => {
    setLoading(true)

    Api.get(authPrefix(role) + "/login-2fa-time-left", {
      params: {
        challenge_id: challenge,
      },
    })
      .then((res) => {
        let seconds = parseSecondsLeft(res.data)

        setDeadline(seconds === null ? 0 : seconds)

        if (res.data && (res.data.sent_to || res.data.email || res.data.phone)) {
          setSentTo(res.data.sent_to || res.data.email || res.data.phone)
        }
      })
      .catch((err) => {
        // невалиден или изтекъл код - остава само бутонът за нов
        setDeadline(0)
        setError(getErrorMessage(err, trans("login.twoFactor.expired", {}, lang)))
      })
      .finally(() => {
        setLoading(false)

        setTimeout(() => {
          focusInput(0)
        }, 0)
      })
  }

  const handleVerify = (value) => {
    submittedRef.current = value

    setVerifying(true)
    setError(null)
    setNotice(null)

    Api.post(authPrefix(role) + "/login-verify", {
      challenge_id: challenge,
      code: value,
      ...(tenant ? { tenant_id: tenant } : {}),
    })
      .then((res) => {
        if (res.status === 200 && res.data.user) {
          onLogin(res.data)
        } else {
          handleWrongCode(trans("login.twoFactor.invalidCode", {}, lang))
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 403) {
          setVerifying(false)
          activationErrorModalRef.current.open()
          return
        }

        handleWrongCode(
          getErrorMessage(err, trans("login.twoFactor.invalidCode", {}, lang))
        )
      })
  }

  const handleWrongCode = (message) => {
    setVerifying(false)
    setError(message)
    setCode(emptyCode())
    submittedRef.current = null

    setTimeout(() => {
      focusInput(0)
    }, 0)
  }

  const handleResend = () => {
    if (resending || cooldown > 0) {
      return
    }

    setResending(true)
    setError(null)
    setNotice(null)

    Api.post(authPrefix(role) + "/login-resend", {
      challenge_id: challenge,
    })
      .then((res) => {
        let newChallenge = res.data?.challenge_id

        setCode(emptyCode())
        submittedRef.current = null
        setCooldown(RESEND_COOLDOWN)
        setNotice(trans("login.twoFactor.codeSent", {}, lang))

        if (newChallenge && String(newChallenge) !== String(challenge)) {
          // новият идентификатор влиза в адреса, а времето се презарежда от ефекта
          history.replace(
            twoFactorPath(role, newChallenge, {
              tenant: tenant,
              lang: lang,
              redirect: searchParams.get("redirect"),
            })
          )

          return
        }

        let seconds = parseSecondsLeft(res.data)

        if (seconds === null) {
          loadTimeLeft()
        } else {
          setDeadline(seconds)

          setTimeout(() => {
            focusInput(0)
          }, 0)
        }
      })
      .catch((err) => {
        setError(
          getErrorMessage(err, trans("login.twoFactor.resendFail", {}, lang))
        )
      })
      .finally(() => {
        setResending(false)
      })
  }

  const handleChange = (e, index) => {
    let value = sanitizeCode(e.target.value)

    if (!value) {
      setCode((prev) => {
        let next = [...prev]
        next[index] = ""
        return next
      })

      return
    }

    setError(null)

    // ако са въведени няколко символа наведнъж, се разпределят напред
    setCode((prev) => {
      let next = [...prev]

      for (let i = 0; i < value.length && index + i < CODE_LENGTH; i++) {
        next[index + i] = value[i]
      }

      return next
    })

    focusInput(Math.min(index + value.length, CODE_LENGTH - 1))
  }

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault()

      setError(null)

      setCode((prev) => {
        let next = [...prev]

        if (next[index]) {
          next[index] = ""
        } else if (index > 0) {
          next[index - 1] = ""
          focusInput(index - 1)
        }

        return next
      })

      return
    }

    if (e.key === "Delete") {
      e.preventDefault()

      setCode((prev) => {
        let next = [...prev]
        next[index] = ""
        return next
      })

      return
    }

    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault()
      focusInput(index - 1)
    }

    if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      e.preventDefault()
      focusInput(index + 1)
    }

    if (e.key === "Home") {
      e.preventDefault()
      focusInput(0)
    }

    if (e.key === "End") {
      e.preventDefault()
      focusInput(CODE_LENGTH - 1)
    }
  }

  const handlePaste = (e, index) => {
    let text = sanitizeCode(e.clipboardData.getData("text"))

    if (!text) {
      return
    }

    e.preventDefault()

    setError(null)

    // пълният код се попълва от първото поле
    let start = text.length >= CODE_LENGTH ? 0 : index

    setCode((prev) => {
      let next = [...prev]

      for (let i = 0; i < text.length && start + i < CODE_LENGTH; i++) {
        next[start + i] = text[i]
      }

      return next
    })

    focusInput(Math.min(start + text.length, CODE_LENGTH - 1))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    let value = code.join("")

    if (value.length === CODE_LENGTH && !disabled) {
      handleVerify(value)
    }
  }

  const handleLangChange = (e) => {
    setLang(e.target.value)
  }

  const onStorageUpdate = (e) => {
    const { key } = e

    if (key === "auth") {
      window.location = "/"
    }
  }

  const countdownClass = () => {
    if (expired) {
      return " expired"
    }

    if (secondsLeft !== null && secondsLeft <= 30) {
      return " warning"
    }

    return ""
  }

  return (
    <>
      <ActivationError ref={activationErrorModalRef} />

      <section id="login">
        <img src={brandImage} alt="brand" />

        <form onSubmit={handleSubmit} className="two-factor">
          <div className="top">
            <Link to={"/login/" + (role || "partners")} className="back">
              {trans("login.twoFactor.back", {}, lang)}
            </Link>
            <select value={lang} onChange={handleLangChange}>
              {languages.map((language) => (
                <option key={language.id} value={language.code}>
                  {language.name}
                </option>
              ))}
            </select>
          </div>

          <h1>{trans("login.twoFactor.head", {}, lang)}</h1>
          <p>
            {sentTo
              ? trans("login.twoFactor.descriptionSentTo", { sentTo: sentTo }, lang)
              : trans("login.twoFactor.description", {}, lang)}
          </p>

          {loading ? (
            <div className="loading">
              <img src={loader} alt="loading" />
            </div>
          ) : (
            <>
              <div className={"countdown" + countdownClass()}>
                <span className="value">{formatSeconds(secondsLeft)}</span>
                <small>
                  {expired
                    ? trans("login.twoFactor.timeIsUp", {}, lang)
                    : trans("login.twoFactor.timeLeft", {}, lang)}
                </small>
              </div>

              {!expired && (
                <div className={"code" + (error ? " invalid" : "")}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputsRef.current[index] = el)}
                      type="text"
                      className={digit ? "filled" : ""}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      aria-label={trans(
                        "login.twoFactor.digit",
                        { index: index + 1 },
                        lang
                      )}
                      maxLength={CODE_LENGTH}
                      value={digit}
                      // при проверка полетата не се заключват с disabled,
                      // за да не се губи фокусът при грешен код
                      readOnly={verifying}
                      autoFocus={index === 0}
                      onChange={(e) => handleChange(e, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={(e) => handlePaste(e, index)}
                      onFocus={(e) => e.target.select()}
                    />
                  ))}
                </div>
              )}

              {error ? (
                <div className="message error">{error}</div>
              ) : notice ? (
                <div className="message success">{notice}</div>
              ) : (
                <></>
              )}

              {verifying && (
                <div className="loading">
                  <img src={loader} alt="loading" />
                </div>
              )}

              <div className="actions">
                <span className="hint">
                  {trans("login.twoFactor.noCode", {}, lang)}
                </span>
                <button
                  type="button"
                  className={
                    "button " + (expired ? "button-primary" : "button-secondary")
                  }
                  disabled={resending || cooldown > 0}
                  onClick={handleResend}
                >
                  {cooldown > 0
                    ? trans("login.twoFactor.resendIn", { seconds: cooldown }, lang)
                    : trans("login.twoFactor.resend", {}, lang)}
                </button>
              </div>
            </>
          )}
        </form>

        <p>© EUS {moment().format("YYYY")}. All rights reserved.</p>
      </section>
    </>
  )
}

export default TwoFactor
